/**
 * Modeville product gallery: swipe and zoom dialog.
 * Extends the core class from mv-gallery.js and registers <mv-gallery>.
 */
(() => {
  'use strict';
  const Base = window.MvGalleryBase;
  if (!Base || customElements.get('mv-gallery')) return;

  const SWIPE_PX = 40;
  const sized = Base.sized;

  class MvGallery extends Base {
    bindSwipe() {
      const stage = this.stage;
      if (!stage) return;

      stage.addEventListener('pointerdown', (event) => {
        if (event.button !== 0 || (event.target instanceof Element && event.target.closest('button'))) return;
        this.drag = { id: event.pointerId, x: event.clientX, y: event.clientY, moved: false };
      });

      stage.addEventListener('pointermove', (event) => {
        const drag = this.drag;
        if (!drag || drag.id !== event.pointerId) return;
        const dx = event.clientX - drag.x;
        const dy = event.clientY - drag.y;
        if (Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(dy)) drag.moved = true;
      });

      const finish = (event) => {
        const drag = this.drag;
        if (!drag || drag.id !== event.pointerId) return;
        this.drag = null;
        const dx = event.clientX - drag.x;
        if (drag.moved) {
          this.lastDragAt = Date.now();
          if (event.type === 'pointerup' && Math.abs(dx) >= SWIPE_PX) this.manualStep(dx < 0 ? 1 : -1);
        }
      };
      stage.addEventListener('pointerup', finish);
      stage.addEventListener('pointercancel', finish);

      this.frame?.addEventListener('click', () => {
        if (Date.now() - this.lastDragAt < 400) return;
        this.openZoom();
      });
    }

    bindZoom() {
      if (!this.dialog) return;

      this.querySelector('[data-zoom-open]')?.addEventListener('click', () => this.openZoom());
      this.querySelector('[data-zoom-close]')?.addEventListener('click', () => this.dialog.close());
      this.querySelector('[data-zoom-prev]')?.addEventListener('click', () => this.zoomStep(-1));
      this.querySelector('[data-zoom-next]')?.addEventListener('click', () => this.zoomStep(1));

      this.dialog.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') this.zoomStep(-1);
        else if (event.key === 'ArrowRight') this.zoomStep(1);
      });

      this.dialog.addEventListener('close', () => this.dialog.classList.remove('is-zoomed'));

      // Click the photo to toggle 2x zoom, drag to pan while zoomed.
      let pan = null;
      this.zoomScroller?.addEventListener('pointerdown', (event) => {
        if (!this.dialog.classList.contains('is-zoomed')) return;
        pan = { x: event.clientX, y: event.clientY, left: this.zoomScroller.scrollLeft, top: this.zoomScroller.scrollTop, moved: false };
        this.zoomScroller.setPointerCapture(event.pointerId);
      });
      this.zoomScroller?.addEventListener('pointermove', (event) => {
        if (!pan) return;
        const dx = event.clientX - pan.x;
        const dy = event.clientY - pan.y;
        if (Math.abs(dx) + Math.abs(dy) > 4) pan.moved = true;
        this.zoomScroller.scrollLeft = pan.left - dx;
        this.zoomScroller.scrollTop = pan.top - dy;
      });
      const endPan = () => {
        if (pan?.moved) this.zoomPanEndedAt = Date.now();
        pan = null;
      };
      this.zoomScroller?.addEventListener('pointerup', endPan);
      this.zoomScroller?.addEventListener('pointercancel', endPan);

      this.zoomScroller?.addEventListener('click', (event) => {
        if (Date.now() - (this.zoomPanEndedAt || 0) < 300) return;
        if (event.target !== this.zoomImg && event.target !== this.zoomScroller) return;
        const zoomed = this.dialog.classList.contains('is-zoomed');
        if (zoomed) {
          this.dialog.classList.remove('is-zoomed');
          return;
        }
        const rect = this.zoomImg.getBoundingClientRect();
        const fx = rect.width ? (event.clientX - rect.left) / rect.width : 0.5;
        const fy = rect.height ? (event.clientY - rect.top) / rect.height : 0.5;
        this.dialog.classList.add('is-zoomed');
        const scroller = this.zoomScroller;
        scroller.scrollLeft = fx * scroller.scrollWidth - scroller.clientWidth / 2;
        scroller.scrollTop = fy * scroller.scrollHeight - scroller.clientHeight / 2;
      });

      // Click on the empty backdrop area closes the dialog.
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.dialog.close();
      });
    }

    /* -------------------------------------------------------------------- zoom */

    openZoom() {
      if (!this.zoomEnabled || !this.dialog || this.dialog.open) return;
      this.stopAutoplay();
      this.renderZoom();
      if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
      else this.dialog.setAttribute('open', '');
    }

    renderZoom() {
      const item = this.current;
      this.dialog.classList.remove('is-zoomed');
      this.zoomImg.alt = `${this.dataset.title || document.title.split(' – ')[0] || 'Product'} photo ${this.items.indexOf(item) + 1}`;
      this.zoomImg.src = sized(item.src, 2000);
    }

    /**
     * @param {number} direction
     */
    zoomStep(direction) {
      const visible = this.visible();
      if (visible.length < 2) return;
      const index = Math.max(0, visible.indexOf(this.current));
      const item = visible[(index + direction + visible.length) % visible.length];
      this.show(item, { announce: true, instant: true });
      this.renderZoom();
    }
  }

  customElements.define('mv-gallery', MvGallery);
})();